/**
 * Lista de bolsas del depósito. Desde 2026-09-10 muestra el correo de cada bolsa (chip con su
 * color) y permite filtrar por correo. Fuente: GET /api/warehouse/bags/recent (trae `courier`).
 */
import { useState, useEffect, useMemo } from 'react';
import { BagDetail } from './BagDetail';
import type { WhCourier } from '../shared';

interface BagRow {
  id: number;
  bag_code: string;
  status: 'open' | 'closed';
  order_count: number;
  item_count: number;
  courier_id: number | null;
  courier: WhCourier | null;
}

export function BagsList({ refreshKey }: { refreshKey?: number }) {
  const [bags, setBags] = useState<BagRow[]>([]);
  const [filter, setFilter] = useState<'all' | number>('all');

  useEffect(() => {
    fetch(`/api/warehouse/bags/recent?limit=50`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setBags(d.bags ?? []))
      .catch(() => setBags([]));
  }, [refreshKey]);

  const couriers = useMemo(() => {
    const map = new Map<number, WhCourier>();
    for (const b of bags) if (b.courier) map.set(b.courier.id, b.courier);
    return [...map.values()];
  }, [bags]);

  const visible = filter === 'all' ? bags : bags.filter((b) => b.courier_id === filter);

  if (bags.length === 0) {
    return <div className="p-4 text-sm text-gray-500">Sin bolsas todavía.</div>;
  }

  return (
    <div className="space-y-2">
      {couriers.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-[var(--color-text-secondary)]">Correo:</span>
          <button
            onClick={() => setFilter('all')}
            className={`rounded px-2 py-0.5 ${filter === 'all' ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-800'}`}
          >
            Todos
          </button>
          {couriers.map((c) => (
            <button
              key={c.id}
              onClick={() => setFilter(c.id)}
              className="rounded px-2 py-0.5 text-white"
              style={{ backgroundColor: c.color, opacity: filter === 'all' || filter === c.id ? 1 : 0.45 }}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}
      {visible.map((b) => (
        <details key={b.id} className="rounded border border-gray-200 bg-white">
          <summary className="cursor-pointer px-4 py-2 hover:bg-gray-50">
            <span className="font-mono font-semibold">{b.bag_code}</span>
            {b.courier && (
              <span
                className="ml-2 rounded px-2 py-0.5 text-xs font-semibold text-white"
                style={{ backgroundColor: b.courier.color }}
              >
                {b.courier.name}
              </span>
            )}
            <span
              className={`ml-2 rounded px-2 py-0.5 text-xs ${
                b.status === 'open' ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'
              }`}
            >
              {b.status}
            </span>
            <span className="ml-2 text-sm text-gray-600">
              {b.order_count} órdenes · {b.item_count} items
            </span>
          </summary>
          <BagDetail bagId={b.id} />
        </details>
      ))}
    </div>
  );
}
